import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { CheckCircle2, Circle, GripVertical, Plus } from 'lucide-react';
import { Avatar, LabelChip, PriorityDot, fmtDate } from './ui';

function orderBetween(list, index) {
  // list = target column WITHOUT the moved task, index = insertion point
  const prev = index > 0 ? list[index - 1].order : null;
  const next = index < list.length ? list[index].order : null;
  if (prev === null && next === null) return 1000;
  if (prev === null) return next / 2;
  if (next === null) return prev + 1000;
  return (prev + next) / 2;
}

function TaskCard({ task, roster, labels, onOpen, onToggleComplete, readOnly, dragging, groupIsDone }) {
  const done = Boolean(task.completed_at) || Boolean(groupIsDone);
  const taskLabels = (task.labels || [])
    .map((id) => labels.find((l) => l.id === id))
    .filter(Boolean);
  const assignees = (task.assignee_mids || [])
    .map((mid) => roster.find((m) => m.mid === mid))
    .filter(Boolean);
  const overdue = task.due_date && !done && new Date(task.due_date) < new Date();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(task.id)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(task.id)}
      className={`rounded-md border border-pm-border bg-pm-raised p-3 text-left shadow-[var(--pm-elev-1)] ${
        dragging ? 'opacity-90 shadow-[var(--pm-elev-3)] ring-1 ring-[var(--pm-focus-border)]' : 'hover:border-[var(--pm-hover-border)] hover:shadow-[var(--pm-elev-2)]'
      } ${done ? 'opacity-60' : ''}`}
    >
      {taskLabels.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {taskLabels.map((l) => (
            <LabelChip key={l.id} label={l} small />
          ))}
        </div>
      )}
      <div className="flex items-start gap-2">
        {!readOnly && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleComplete(task);
            }}
            className={`mt-0.5 shrink-0 ${done ? 'text-pm-accent' : 'text-pm-faint hover:text-pm-accent'}`}
            title={done ? 'Reopen' : 'Complete'}
          >
            {done ? <CheckCircle2 size={15} /> : <Circle size={15} />}
          </button>
        )}
        <span className={`min-w-0 text-sm leading-snug ${done ? 'line-through' : ''}`}>{task.title}</span>
      </div>
      {(assignees.length > 0 || task.due_date || task.priority !== 'none' || (task.subtasks || []).length > 0) && (
        <div className="mt-2 flex items-center gap-2 text-xs text-pm-faint">
          {task.due_date && (
            <span style={overdue ? { color: 'var(--pm-danger)' } : undefined}>{fmtDate(task.due_date)}</span>
          )}
          <PriorityDot priority={task.priority} />
          {(task.subtasks || []).length > 0 && (
            <span className="pm-num">
              {(task.subtasks || []).filter((s) => s.done).length}/{(task.subtasks || []).length}
            </span>
          )}
          <span className="ml-auto flex -space-x-1.5">
            {assignees.slice(0, 3).map((m) => (
              <Avatar key={m.mid} name={m.name} size={18} />
            ))}
          </span>
        </div>
      )}
    </div>
  );
}

function SortableCard(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task.id,
    disabled: props.readOnly,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      {...attributes}
      {...listeners}
    >
      <TaskCard {...props} />
    </div>
  );
}

// A whole column is sortable (drag the grip in its header to reorder columns);
// the cards inside stay independently sortable via their own SortableContext.
function Column({ group, children, count, onQuickAdd, readOnly }) {
  const cardsDrop = useDroppable({ id: `group:${group.id}` });
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: `col:${group.id}`, disabled: readOnly });
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await onQuickAdd(group.id, title.trim());
    setTitle('');
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex w-72 shrink-0 flex-col overflow-hidden rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]"
    >
      {/* Group-color strip across the column top (the Nifty column signature) —
          replaces the old inline dot as the column's color carrier. */}
      <span className="h-0.5 w-full shrink-0" style={{ background: group.color }} aria-hidden />
      <div className="flex items-center gap-2 px-3 py-2.5">
        {!readOnly && (
          <button
            type="button"
            className="-ml-1 cursor-grab touch-none text-pm-faint hover:text-pm-text active:cursor-grabbing"
            title="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={14} />
          </button>
        )}
        <span className="text-sm font-semibold">{group.name}</span>
        <span className="pm-num inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-pm-raised px-1.5 text-[11px] font-medium text-pm-muted">
          {count}
        </span>
      </div>
      <div ref={cardsDrop.setNodeRef} className="flex min-h-[40px] flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">
        {children}
      </div>
      {!readOnly && (
        <div className="border-t border-pm-border p-2">
          {adding ? (
            <form onSubmit={submit}>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => !title.trim() && setAdding(false)}
                onKeyDown={(e) => e.key === 'Escape' && setAdding(false)}
                placeholder="Task title, Enter to add"
                autoFocus
                className="w-full rounded-md border border-pm-border bg-pm-input px-2 py-1.5 text-sm outline-none transition-colors focus:border-[var(--pm-focus-border)]"
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex w-full items-center justify-center gap-1.5 rounded-md bg-pm-raised px-2 py-1.5 text-xs font-medium text-pm-accent hover:brightness-[1.08] active:translate-y-px"
            >
              <Plus size={13} />
              Add task
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function KanbanBoard({ groups, tasks, roster, labels, readOnly, onOpen, onCreate, onMove, onToggleComplete, onReorderGroups }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byGroup = useMemo(() => {
    const map = {};
    groups.forEach((g) => {
      map[g.id] = tasks.filter((t) => t.group_id === g.id).sort((a, b) => a.order - b.order);
    });
    return map;
  }, [groups, tasks]);

  const findGroupOf = (taskId) => tasks.find((t) => t.id === taskId)?.group_id;

  const resolveTarget = (over) => {
    if (!over) return null;
    const id = String(over.id);
    if (id.startsWith('group:')) return { groupId: id.slice(6), overTaskId: null };
    if (id.startsWith('col:')) return { groupId: id.slice(4), overTaskId: null };
    return { groupId: findGroupOf(id), overTaskId: id };
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    const activeIdStr = String(active.id);

    // column reorder (item 1) — the active id is prefixed col:
    if (activeIdStr.startsWith('col:')) {
      if (!over) return;
      const overStr = String(over.id);
      const activeGid = activeIdStr.slice(4);
      const overGid = overStr.startsWith('col:') ? overStr.slice(4)
        : overStr.startsWith('group:') ? overStr.slice(6) : findGroupOf(overStr);
      if (!overGid || activeGid === overGid) return;
      const ids = groups.map((g) => g.id);
      const from = ids.indexOf(activeGid);
      const to = ids.indexOf(overGid);
      if (from < 0 || to < 0) return;
      onReorderGroups(arrayMove(ids, from, to));
      return;
    }

    // task move (existing behavior)
    const target = resolveTarget(over);
    if (!target || !target.groupId) return;
    const taskId = activeIdStr;
    const column = (byGroup[target.groupId] || []).filter((t) => t.id !== taskId);
    let index = column.length;
    if (target.overTaskId && target.overTaskId !== taskId) {
      index = column.findIndex((t) => t.id === target.overTaskId);
      if (index < 0) index = column.length;
    }
    const order = orderBetween(column, index);
    const task = tasks.find((t) => t.id === taskId);
    if (task && (task.group_id !== target.groupId || task.order !== order)) {
      onMove(taskId, target.groupId, order);
    }
  };

  const activeTask = activeId && !activeId.startsWith('col:') ? tasks.find((t) => t.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => setActiveId(String(active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={groups.map((g) => `col:${g.id}`)} strategy={horizontalListSortingStrategy}>
        <div className="flex h-full gap-3 overflow-x-auto pb-2">
          {groups.map((g) => (
            <Column
              key={g.id}
              group={g}
              count={(byGroup[g.id] || []).length}
              onQuickAdd={onCreate}
              readOnly={readOnly}
            >
              <SortableContext items={(byGroup[g.id] || []).map((t) => t.id)} strategy={verticalListSortingStrategy}>
                {(byGroup[g.id] || []).map((t) => (
                  <SortableCard
                    key={t.id}
                    task={t}
                    roster={roster}
                    labels={labels}
                    readOnly={readOnly}
                    groupIsDone={g.is_done_group}
                    onOpen={onOpen}
                    onToggleComplete={onToggleComplete}
                  />
                ))}
              </SortableContext>
            </Column>
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeTask && (
          <TaskCard task={activeTask} roster={roster} labels={labels} readOnly onOpen={() => {}} onToggleComplete={() => {}} dragging />
        )}
      </DragOverlay>
    </DndContext>
  );
}
